import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Divider
} from '@mui/material';
import { Home, Add, Edit, Delete, PostAdd, SwapHoriz } from '@mui/icons-material';
import axios from 'axios';

import { sym } from '../utils/currency';
const STATUS_COLOR = { Open: 'primary', Posted: 'success', Cancelled: 'default' };

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const TransferOrderList = () => {
  const navigate = useNavigate();
  const [transfers, setTransfers]     = useState([]);
  const [error,     setError]         = useState('');
  const [success,   setSuccess]       = useState('');
  const [loading,   setLoading]       = useState(true);
  const [viewTransfer, setViewTransfer] = useState(null);
  const [confirm,   setConfirm]       = useState({ open: false, title: '', message: '', onOk: null });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 1000);
    return () => clearTimeout(t);
  }, [success]);

  const fetchTransfers = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/procurement/transfers`, { headers }
      );
      setTransfers(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load transfer orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTransfers(); }, []); // eslint-disable-line

  const openConfirm = (title, message, onOk) =>
    setConfirm({ open: true, title, message, onOk });

  const closeConfirm = () => setConfirm({ open: false, title: '', message: '', onOk: null });

  const handlePost = (transferNo) => {
    openConfirm(
      `Post ${transferNo}`,
      `Posting will update stock at both locations and create GL entries for Transfer Order ${transferNo}. This cannot be undone.`,
      async () => {
        closeConfirm();
        try {
          const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/procurement/transfers/${transferNo}/post`,
            {}, { headers }
          );
          setSuccess(data.message);
          fetchTransfers();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to post transfer order');
        }
      }
    );
  };

  const handleCancel = (transferNo) => {
    openConfirm(
      `Cancel ${transferNo}`,
      `Are you sure you want to cancel Transfer Order ${transferNo}?`,
      async () => {
        closeConfirm();
        try {
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/api/procurement/transfers/${transferNo}`, { headers }
          );
          setSuccess('Transfer Order cancelled');
          fetchTransfers();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to cancel transfer order');
        }
      }
    );
  };

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading transfer orders...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <SwapHoriz sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">Transfer Orders</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/procurement')} title="Procurement Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={() => navigate('/procurement/transfers/new')} title="New Transfer Order"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {transfers.length === 0 ? (
            <Typography color="text.secondary">No transfer orders yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Transfer No</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>From</strong></TableCell>
                  <TableCell><strong>To</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="center"><strong>Lines</strong></TableCell>
                  <TableCell align="right"><strong>Total ({sym()})</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transfers.map(tr => (
                  <TableRow key={tr.transferNo} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewTransfer(tr)}>
                    <TableCell sx={{ fontFamily: 'monospace' }}><strong>{tr.transferNo}</strong></TableCell>
                    <TableCell>
                      {tr.transferDate ? new Date(tr.transferDate).toLocaleDateString() : ''}
                    </TableCell>
                    <TableCell>{tr.fromLocationCode || '—'}</TableCell>
                    <TableCell>{tr.toLocationCode   || '—'}</TableCell>
                    <TableCell>{tr.description}</TableCell>
                    <TableCell align="center">{tr.lines?.length || 0}</TableCell>
                    <TableCell align="right">{(tr.totalAmount || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip label={tr.status} color={STATUS_COLOR[tr.status] || 'default'}
                        size="small" variant="outlined" />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {tr.status === 'Open' && (
                        <>
                          <IconButton size="small" color="success" title="Post"
                            onClick={() => handlePost(tr.transferNo)} sx={{ mr: 0.5 }}>
                            <PostAdd fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="primary" title="Edit"
                            onClick={() => navigate(`/procurement/transfers/${tr.transferNo}/edit`)} sx={{ mr: 0.5 }}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" title="Cancel"
                            onClick={() => handleCancel(tr.transferNo)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Transfer Card (view) ── */}
      <Dialog open={Boolean(viewTransfer)} onClose={() => setViewTransfer(null)} maxWidth="sm" fullWidth>
        {viewTransfer && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <SwapHoriz color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewTransfer.transferNo}</Typography>
                  <Typography variant="caption" color="text.secondary">Transfer Order</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewTransfer.status} color={STATUS_COLOR[viewTransfer.status] || 'default'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Document</Typography>
              <InfoRow label="Transfer No"  value={viewTransfer.transferNo} />
              <InfoRow label="Date"         value={viewTransfer.transferDate ? new Date(viewTransfer.transferDate).toLocaleDateString() : ''} />
              <InfoRow label="Description"  value={viewTransfer.description} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Movement</Typography>
              <InfoRow label="From Location" value={viewTransfer.fromLocationCode} />
              <InfoRow label="To Location"   value={viewTransfer.toLocationCode} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Totals</Typography>
              <InfoRow label="No. of Lines" value={String(viewTransfer.lines?.length || 0)} />
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>
                  Total Amount ({sym()})
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {(viewTransfer.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              {viewTransfer.status === 'Open' && (
                <>
                  <Button color="primary"
                    onClick={() => { setViewTransfer(null); navigate(`/procurement/transfers/${viewTransfer.transferNo}/edit`); }}>
                    Edit
                  </Button>
                  <Button color="success" variant="outlined"
                    onClick={() => { setViewTransfer(null); handlePost(viewTransfer.transferNo); }}>
                    Post
                  </Button>
                  <Button color="error"
                    onClick={() => { setViewTransfer(null); handleCancel(viewTransfer.transferNo); }}>
                    Cancel
                  </Button>
                </>
              )}
              <Button variant="outlined" onClick={() => setViewTransfer(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirm.onOk}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransferOrderList;
